import { useCallback, useEffect } from "react"

import { useLocation, useNavigate, useParams } from "react-router-dom"

import useBatteryProposalFinancing from "~/api/hooks/useBatteryProposalFinancing"
import { Drawer } from "~/components/ui-components/v2"

import { Eyebrow } from "../primitives"
import { batteryDrawerReturnTo } from "./battery-drawer-launch"
import { LIGHTREACH_BATTERY_FINANCE_TYPE } from "../lightreach-battery"
import { batteryProposalPath } from "../setup/setup-steps"
import { CONCERT_BATTERY_FINANCE_TYPE } from "./concert/concert-battery-copy"
import { ConcertBatteryFinancingStatus } from "./concert/ConcertBatteryApplicationPanel"
import { CreditHumanFinancingStatus } from "./credit-human/CreditHumanFinancingStatus"
import {
  batteryFinancingStatusExperienceFor,
  CREDIT_HUMAN_BATTERY_FINANCE_TYPE,
  registerBatteryFinancingStatusExperience,
  resolveBatteryFinancingStatusExperience,
} from "./financing-status-experiences"
import { LIGHTREACH_EYEBROW } from "./lightreach/lightreach-battery-copy"
import { LightReachBatteryFinancingStatus } from "./lightreach/LightReachBatteryFinancingStatus"
import { SUNGAGE_BATTERY_FINANCE_TYPE } from "../sungage/sungage-battery-copy"
import { SungageBatteryFinancingStatus } from "../sungage/SungageBatteryApplicationStatus"

const registerExperiences = (): void => {
  registerBatteryFinancingStatusExperience(
    CREDIT_HUMAN_BATTERY_FINANCE_TYPE,
    CreditHumanFinancingStatus,
  )
  registerBatteryFinancingStatusExperience(
    CONCERT_BATTERY_FINANCE_TYPE,
    ConcertBatteryFinancingStatus,
  )
  registerBatteryFinancingStatusExperience(
    SUNGAGE_BATTERY_FINANCE_TYPE,
    SungageBatteryFinancingStatus,
  )
  registerBatteryFinancingStatusExperience(
    LIGHTREACH_BATTERY_FINANCE_TYPE,
    LightReachBatteryFinancingStatus,
  )
}

registerExperiences()

/**
 * Routed Battery financing-status drawer. Selects a Finance Partner experience
 * from the registry; Contract status never branches on partner here.
 */
export const BatteryFinancingStatusDrawer = (): JSX.Element | null => {
  const { proposalId } = useParams<{ proposalId: string }>()
  const navigate = useNavigate()
  const { state } = useLocation()
  const financingQuery = useBatteryProposalFinancing(proposalId)

  const onClose = useCallback(() => {
    if (!proposalId) {
      return
    }
    navigate(batteryDrawerReturnTo(state, batteryProposalPath(proposalId)))
  }, [navigate, proposalId, state])

  useEffect(() => {
    // Registration is idempotent; ensures hot reload keeps partners wired.
    registerExperiences()
  }, [])

  if (!proposalId) {
    return null
  }

  const financing = financingQuery.data
  const Experience = resolveBatteryFinancingStatusExperience(financing)
  const creditHumanExperience = batteryFinancingStatusExperienceFor(
    CREDIT_HUMAN_BATTERY_FINANCE_TYPE,
  )
  const title =
    Experience != null && Experience === creditHumanExperience
      ? `Credit Human`
      : Experience != null &&
          Experience ===
            batteryFinancingStatusExperienceFor(CONCERT_BATTERY_FINANCE_TYPE)
        ? `Concert`
        : Experience != null &&
            Experience ===
              batteryFinancingStatusExperienceFor(SUNGAGE_BATTERY_FINANCE_TYPE)
          ? `Sungage Brightstart (Battery Only)`
          : Experience != null &&
              Experience ===
                batteryFinancingStatusExperienceFor(
                  LIGHTREACH_BATTERY_FINANCE_TYPE,
                )
            ? LIGHTREACH_EYEBROW
            : `Application status`

  return (
    <Drawer
      open
      theme="dark"
      onClose={onClose}
      title="Application status"
      widthClass="sm:max-w-xl"
      hideCloseButton
      contentClassName="flex flex-col pb-0"
      header={({ close }) => (
        <div className="border-b border-bp-border bg-bp-base px-6 pt-5 pb-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Eyebrow className="mb-1">{title}</Eyebrow>
              <h2 className="font-display text-[length:var(--step-2)] font-semibold text-bp-text">
                Application status
              </h2>
              <p className="mt-1 text-[length:var(--step--1)] text-bp-text-muted">
                {financingQuery.isError
                  ? `Unable to load financing status.`
                  : financing == null
                    ? `Loading financing…`
                    : Experience == null
                      ? `No application status experience for this Finance Partner yet.`
                      : `Existing Loan Application and financing snapshot.`}
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              data-testid="battery-financing-status-close"
              className="rounded-md px-2 py-1 text-[length:var(--step--2)] font-semibold text-bp-text-muted hover:text-bp-text"
            >
              Close
            </button>
          </div>
        </div>
      )}
    >
      {financing == null ? (
        <div
          role={financingQuery.isError ? `alert` : `status`}
          data-testid="battery-financing-status-loading"
          className="flex-1 text-[length:var(--step--1)] text-bp-text-muted"
        >
          {financingQuery.isError
            ? `Financing could not be loaded. Close and try again.`
            : `Loading financing status…`}
        </div>
      ) : Experience != null ? (
        <Experience proposalId={proposalId} financing={financing} />
      ) : (
        <p
          data-testid="battery-financing-status-unsupported"
          className="text-[length:var(--step--1)] text-bp-text-muted"
        >
          Application status for this Finance Partner is not available in Vision
          yet.
        </p>
      )}
    </Drawer>
  )
}
